<template>
    <div class="section">
        <div class="center-section">
            <div class="form-heading">
                <h1 class="header-1">{{ $t('auth.reset.createNewPassword') }}</h1>
                <p class="caption">
                    {{ $t('auth.reset.securePassword') }}
                </p>
            </div>
           
            <div class="signup-form">
                <div v-if="error" class="error-message">
                    <BaseIcon iconName="error" color="#ff6b6b" />
                    {{ error }}
                </div>

                <div v-if="successMessage" class="success-message">
                    <BaseIcon iconName="check" color="#20AD8B" />
                    {{ successMessage }}
                </div>

                <form @submit.prevent="handleSubmit">
                    <div class="password-section">
                        <base-input 
                            class="form-group" 
                            type="password" 
                            :placeholder="$t('auth.reset.newPassword')"
                            required 
                            icon="lock"
                            v-model="newPassword"
                            :error="passwordError"
                        />
                        
                        <div class="password-strength" v-if="newPassword">
                            <div class="strength-item" :class="{ active: hasMinLength }">
                                <BaseIcon :iconName="hasMinLength ? 'check' : 'circle'" />
                                {{ $t('auth.passwordRules.minLength') }}
                            </div>
                            <div class="strength-item" :class="{ active: hasUpperCase }">
                                <BaseIcon :iconName="hasUpperCase ? 'check' : 'circle'" />
                                {{ $t('auth.passwordRules.uppercase') }}
                            </div>
                            <div class="strength-item" :class="{ active: hasLowerCase }">
                                <BaseIcon :iconName="hasLowerCase ? 'check' : 'circle'" />
                                {{ $t('auth.passwordRules.lowercase') }}
                            </div>
                            <div class="strength-item" :class="{ active: hasNumber }">
                                <BaseIcon :iconName="hasNumber ? 'check' : 'circle'" />
                                {{ $t('auth.passwordRules.number') }}
                            </div>
                        </div>

                        <base-input 
                            class="form-group" 
                            type="password" 
                            :placeholder="$t('auth.signup.confirmPassword')"
                            required 
                            icon="lock"
                            v-model="confirmPassword"
                            :error="confirmError"
                        />
                    </div>

                    <primary-btn :disabled="!isValid || isLoading">
                        {{ isLoading ? $t('auth.reset.updating') : $t('auth.reset.update') }}
                    </primary-btn>
                </form>
            </div>

            <div class="login-link caption">
                <router-link to="/login" class="link">
                    {{ $t('auth.reset.backToLogin') }}
                </router-link>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import BaseInput from '@/components/BaseComponents/BaseInput.vue';
import PrimaryBtn from '@/components/BaseComponents/PrimaryBtn.vue';
import BaseIcon from '@/components/BaseComponents/BaseIcon.vue';
import { authAPI } from '@/services/api';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    token: {
        type: String,
        required: true
    }
});

const router = useRouter();
const { t } = useI18n();

const newPassword = ref('');
const confirmPassword = ref('');
const error = ref('');
const successMessage = ref('');
const isLoading = ref(false);

// Password strength indicators
const hasMinLength = computed(() => newPassword.value.length >= 8);
const hasUpperCase = computed(() => /[A-Z]/.test(newPassword.value));
const hasLowerCase = computed(() => /[a-z]/.test(newPassword.value));
const hasNumber = computed(() => /[0-9]/.test(newPassword.value));

onMounted(() => {
    if (!props.token) {
        error.value = t('auth.reset.invalidLink');
        setTimeout(() => router.push('/resetpass'), 2000);
    }
});

const passwordError = computed(() => {
    if (!newPassword.value) return '';
    const errors = [];
    
    if (newPassword.value.length < 8) {
        errors.push(t('auth.passwordRules.minLength'));
    }
    if (!/[A-Z]/.test(newPassword.value)) {
        errors.push(t('auth.passwordRules.uppercase'));
    }
    if (!/[a-z]/.test(newPassword.value)) {
        errors.push(t('auth.passwordRules.lowercase'));
    }
    if (!/[0-9]/.test(newPassword.value)) {
        errors.push(t('auth.passwordRules.number'));
    }

    if (errors.length > 0) {
        return `${t('auth.password')} ${errors.join(', ')}`;
    }
    return '';
});

const confirmError = computed(() => {
    if (!confirmPassword.value) return '';
    if (confirmPassword.value !== newPassword.value) {
        return t('auth.signup.passwordsMismatch');
    }
    return '';
});

const isValid = computed(() => {
    return newPassword.value.length >= 8 &&
           /[A-Z]/.test(newPassword.value) &&
           /[a-z]/.test(newPassword.value) &&
           /[0-9]/.test(newPassword.value) &&
           confirmPassword.value === newPassword.value;
});

async function handleSubmit() {
    if (!isValid.value) {
        error.value = t('auth.reset.requirements');
        return;
    }

    isLoading.value = true;
    error.value = '';
    successMessage.value = '';

    try {
        await authAPI.resetPassword({
            secret_token: props.token,
            new_password: newPassword.value,
            confirm_password: confirmPassword.value
        });

        successMessage.value = t('auth.reset.updated');
        
        setTimeout(() => {
            router.push('/resetpass/success');
        }, 1500);
    } catch (err) {
        console.error('Password reset error:', err);
        if (err.response?.status === 406){
            console.log(err);
            error.value = t('auth.reset.expiredLink');
        } else if (err.response?.status === 400) {
            error.value = t('auth.reset.securityRequirements');
        } else {
            error.value = t('auth.reset.updateFailed');
        }
    } finally {
        isLoading.value = false;
    }
}
</script>

<style scoped>
.section {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--spacing-xl);
}

.center-section {
    width: 35%;
    min-width: 300px;
}

.form-heading {
    text-align: center;
    margin-bottom: var(--spacing-lg);
}

.form-heading h1 {
    margin-bottom: var(--spacing-md);
}

.form-heading .caption {
    color: var(--color-white-40);
}

.signup-form {
    padding: var(--spacing-lg);
    background-color: var(--bg-card);
    border-radius: var(--border-radius-2xl);
    margin-bottom: var(--spacing-lg);
}

.form-group {
    margin-bottom: var(--spacing-lg);
}

.error-message {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    background-color: var(--color-cancel-15);
    color: var(--color-error-light);
    text-align: center;
    margin-bottom: var(--spacing-lg);
    padding: var(--spacing-md);
    border-radius: var(--border-radius-md);
}

.success-message {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    background-color: var(--color-accent-primary-15);
    color: var(--color-accent-primary);
    text-align: center;
    margin-bottom: var(--spacing-lg);
    padding: var(--spacing-md);
    border-radius: var(--border-radius-md);
}

.password-strength {
    background: var(--color-white-05);
    border-radius: var(--border-radius-md);
    padding: var(--spacing-md);
    margin: var(--spacing-sm) 0;
}

.strength-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    color: var(--color-white-60);
    font-size: var(--font-size-description);
    margin: var(--spacing-xs) 0;
}

.strength-item.active {
    color: var(--color-accent-primary);
}

.login-link {
    text-align: center;
    color: var(--color-white-40);
}

.link {
    color: var(--color-white);
    text-decoration: none;
    transition: opacity var(--transition-fast);
}

.link:hover {
    opacity: 0.8;
}

@media (max-width: 768px) {
    .center-section {
        width: 90%;
    }
}
</style>
